/**
 * Leak telemetry.
 *
 * This dashboard is a white-labelled surface. The server is authoritative about
 * what a client may see (invariants I7 and I8), so anything arriving here that
 * the renderer does not recognise is evidence that the filter missed something,
 * not an opportunity to render optimistically.
 *
 * In development we shout. In production we report and draw a neutral
 * placeholder — never the unrecognised content itself, because the whole point
 * is that we do not know whether it is safe to show.
 */

export type LeakKind =
  | 'unknown_stream_event'
  | 'unknown_block_type'
  | 'unknown_author_kind'
  | 'unknown_reason_code'
  | 'unknown_status'
  | 'unknown_error_code'
  | 'blocked_link_scheme'
  | 'render_error';

export interface LeakReport {
  kind: LeakKind;
  /** A short machine token — never the payload itself. */
  token: string;
  context?: Record<string, string | number | boolean | null | undefined>;
}

type Sink = (report: LeakReport) => void;

const sinks: Sink[] = [];

/** Register an additional sink (Sentry, a beacon endpoint, a test spy). */
export function addTelemetrySink(sink: Sink): () => void {
  sinks.push(sink);
  return () => {
    const i = sinks.indexOf(sink);
    if (i >= 0) sinks.splice(i, 1);
  };
}

const seen = new Set<string>();

export function reportLeak(report: LeakReport): void {
  const dedupeKey = `${report.kind}:${report.token}`;

  if (import.meta.env.DEV) {
    // Loud on purpose. A leak that scrolls past in a debug log is a leak that
    // ships.
    // eslint-disable-next-line no-console
    console.error(
      `%c LEAK GUARD %c ${report.kind} → ${report.token}`,
      'background:#a62b1f;color:#fff;font-weight:700;padding:2px 6px;border-radius:3px',
      'color:#a62b1f;font-weight:600',
      report.context ?? {},
    );
  }

  // One report per distinct token per session: a stuck stream would otherwise
  // emit thousands and drown the signal it exists to produce.
  if (seen.has(dedupeKey)) return;
  seen.add(dedupeKey);

  for (const sink of sinks) {
    try {
      sink(report);
    } catch {
      /* a broken sink must never break a render */
    }
  }
}

/** Test/debug helper. Not used by the app. */
export function resetTelemetryDedupe(): void {
  seen.clear();
}
